import type { TeamContribution } from "@/company/core/types";
import { TemperatureGauge, gaugeStatusColor } from "@/company/components/TemperatureGauge";
import { cn } from "@/lib/cn";

interface TeamPaceBarsProps {
  teams: TeamContribution[];
  className?: string;
  /** "tv" = Company Sales TV display (read from across the room). */
  size?: "default" | "tv";
}

const SIZE = {
  default: {
    list: "gap-5 py-2",
    row: "grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_3rem] gap-4",
    dot: "h-2.5 w-2.5",
    text: "text-sm",
    gauge: "lg",
  },
  tv: {
    list: "gap-4",
    row: "grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_5rem] gap-6",
    dot: "h-4 w-4",
    text: "text-2xl",
    gauge: "xl",
  },
} as const;

export function TeamPaceBars({ teams, className, size = "default" }: TeamPaceBarsProps) {
  const styles = SIZE[size];

  return (
    <div className={cn("flex h-full flex-col justify-center", styles.list, className)}>
      {teams.map((team) => (
        <div key={team.teamId} className={cn("grid items-center", styles.row)}>
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn("shrink-0 rounded-full", styles.dot)}
              style={{ backgroundColor: team.color }}
            />
            <span className={cn("truncate font-medium text-[var(--color-text)]", styles.text)}>
              {team.teamName}
            </span>
          </div>
          <TemperatureGauge percentage={team.qtdPacePct} size={styles.gauge} />
          <span
            className={cn(
              "text-right font-bold tabular-nums",
              styles.text,
              gaugeStatusColor(team.qtdPacePct),
            )}
          >
            {team.qtdPacePct}%
          </span>
        </div>
      ))}
    </div>
  );
}
